import { ICancelDialogResult, IDialogProps } from '@tap/tap-ui';
import { DocumentFormSettings, IApiIssue, issueService } from '@tap/data-providers';
import { Grid, Button, DialogActions, DialogContent } from '@mui/material';
import { DialogCustom, DialogTitleCustom } from '@tap/mui-ui';
import { LoadingButton } from '@mui/lab';
import { DocumentForm, TimeTrackingValues, useDocuemtnSchema, useDynamicDocumentForm } from '@tap/doc-ui';

/*
*   Диалог: Редактирование заявки
*/

export type IEditIssueDialogResult = ICancelDialogResult & {
    mode: IssueFormMode,
    newIssueKey?: string,
};

export enum IssueFormMode {
    Create = 'create',
    Edit = 'edit',
    Clone = 'clone',
}

type IEditIssueDialogProps = IDialogProps<{
    documentCode: string,
    timeTrackingValues?: TimeTrackingValues,
    issue?: IApiIssue,
    mode: IssueFormMode,
}, IEditIssueDialogResult>;

export function EditIssueDialog({
    data,
    isOpen,
    handleClose,
}: IEditIssueDialogProps) {
    const documentSchema = useDocuemtnSchema(data.documentCode);

    const handleCancel = () => {
        handleClose({ isCancel: true, mode: data.mode });
    }

    const fields = documentSchema.settings[{
        [IssueFormMode.Create]: 'createFields',
        [IssueFormMode.Edit]: 'editFields',
        [IssueFormMode.Clone]: 'cloneFields',
    }[data.mode] as keyof DocumentFormSettings];

    const formProps = useDynamicDocumentForm({
        documentSchema,
        timeTrackingValues: data.timeTrackingValues,
        documentData: data.issue,
        fields,
        plugins: data.mode === IssueFormMode.Edit ? documentSchema.settings.plugins : [],
        handleSubmit: (values) => {
            //TODO: Check logic with department Ids.
            values.departmentIds = (values.departmentIds?.length > 1 || !values?.data?.department?.id)
                ? values.departmentIds || []
                : [values.data.department.id];

            return Promise.resolve()
                .then(() => {
                    if (data.mode === IssueFormMode.Create)
                        return issueService.createIssue({ documentCode: data.documentCode, statusCode: 'new', priority: 1, ...values });
                    if (data.mode === IssueFormMode.Clone)
                        return issueService.createIssue({ documentCode: data.documentCode, statusCode: 'new', ...values });
                    if (data.mode === IssueFormMode.Edit)
                        return issueService.updateIssue(data.issue.issueKey, values);
                })
                .then((response) => {
                    const callbackData = { isCancel: false, mode: data.mode, newIssueKey: response.issueKey };
                    data.callback && data.callback(callbackData);
                    handleClose(callbackData);
                });
        },
    });

    return (
        <DialogCustom
            handleClose={handleCancel}
            open={isOpen}
        >
            <DialogTitleCustom
                primaryText={
                    {
                        [IssueFormMode.Create]: 'Новая заявка',
                        [IssueFormMode.Edit]: `Редактирование заявки: ${data.issue?.issueKey}`,
                        [IssueFormMode.Clone]: `Клонировать заявку: ${data.issue?.issueKey}`,
                    }[data.mode]
                }
            />
            <DialogContent>

                <Grid
                    item
                    container
                    spacing={2}
                    alignItems='stretch'
                >

                    <DocumentForm {...formProps} />

                </Grid>
            </DialogContent>
            <DialogActions >
                <Grid container justifyContent='flex-end'>

                    <LoadingButton
                        loading={formProps.formik.isSubmitting}
                        disableElevation
                        type='button'
                        variant='contained'
                        color='primary'
                        onClick={formProps.formik.submitForm}
                        sx={{ mr: 2 }}
                    >
                        {{
                            [IssueFormMode.Create]: 'Создать',
                            [IssueFormMode.Edit]: `Сохранить`,
                            [IssueFormMode.Clone]: 'Создать',
                        }[data.mode]}
                    </LoadingButton>

                    <Button
                        disableElevation
                        type='button'
                        variant='text'
                        color='primary'
                        onClick={handleCancel}
                    >
                        Отмена
                    </Button>

                </Grid>
            </DialogActions>
        </DialogCustom >
    );
}
